import type { RouteLocationNormalized, RouteLocationNormalizedLoaded } from "vue-router";
import { appLink, findIpForSPA, findIpForSSR, formatDate, getFullDomain, join, numberFormat, replaceTags, stripTag, uniqueId, getCFImage, domPurify, isIpInCidr, logToFile } from "../libs/utils";
import { useDYErrorStore } from "../stores/error";
import { useDYUserStore } from "../stores/user";
import type { NuxtError, RouteMiddleware } from "nuxt/app";
import { notify } from "@kyvg/vue3-notification";
import { useDYAuth, type AuthCheckParams } from "./auth";
import { useDYAppConfig } from "./config";
import { parserRouteQuery, tryReadJson } from "../libs/utils";
import { $dy__fetch } from "./fetch";
import { isDiscardedRequestError } from "../libs/discarded-request";
import { useDYStructuredDataApi } from "./dy-structured-data";

export interface MakeAuthMiddlewareOptions {
  autoNavigateLoginPage?: boolean
  loginUrl?: string
}



export function useUtils() {
  const appConfig = useDYAppConfig()

  const safeToString = (val: any) => {
    if (val === null || val === undefined) return String(val)
    return typeof val.toString === 'function' ? val.toString() : Object.prototype.toString.call(val)
  }

  /**
   * 라우트 쿼리를 분석해서 현재의 쿼리 결과에 반영해줌 : 기존에는 배열 파라미터처럼, 여러개의 값이 들어가는 경우 정상처리가 되지 않았음
   * @param route 
   * @param withSearchParamNames 
   * @param useFullPath 
   * @returns 
   */
  const makePageKey = (route: RouteLocationNormalizedLoaded, withSearchParamNames: string[] = [], useFullPath: boolean = false) => {
    const params = new URLSearchParams()

    // 안정적인 키 생성을 위해 배열/인덱스형 쿼리(예: lecture[0], lecture[1])까지 수집해 직렬화
    const serializeQueryValue = (name: string) => {
      const value = (route.query as any)[name]
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          return value.map((v) => String(v)).join('|')
        }
        return String(value)
      }

      // name[0], name[1] ... 형태 처리
      const indexedKeys = Object.keys(route.query).filter((key) => key.startsWith(`${name}[`))
      if (indexedKeys.length) {
        const getIndex = (k: string) => Number(k.match(/\[(\d+)\]/)?.[1] ?? 0)
        const arr = indexedKeys
          .sort((a, b) => getIndex(a) - getIndex(b))
          .map((k) => String((route.query as any)[k]))
        return arr.join('|')
      }

      return ''
    }

    withSearchParamNames.forEach((name) => params.set(name, serializeQueryValue(name)))

    const basePath = useFullPath ? route.fullPath.split('#')[0] : route.path
    return basePath.toLowerCase() + '?' + params.toString()
  }

  const getApiUrl = (err: any) => {
    console.log('getApiUrl', err)
    if (!err) return 'N/A'

    // fetch.ts에서 심어준 데이터나 ofetch의 기본 속성 확인
    let foundUrl = err.requestUrl ||
      err.data?.requestUrl ||
      err.request ||
      err.url ||
      err.response?.url ||
      err.response?.request?.url || // ofetch response 객체 내부 확인
      (err.data && err.data.url)

    // Request 객체인 경우 문자열로 변환
    if (foundUrl && typeof foundUrl !== 'string') {
      foundUrl = safeToString(foundUrl)
    }

    // 그래도 없으면 에러 메시지에서 URL 추출 시도 ([GET] "URL" ... 형태)
    if (!foundUrl || foundUrl === '[object Object]') {
      const msg = err.message || safeToString(err)
      // 따옴표로 감싸진 URL 패턴 추출 (http://... 또는 /...)
      const match = typeof msg === 'string' ? msg.match(/["']((?:https?:\/\/|\/)[^"']+)["']/) : null
      if (match) {
        foundUrl = match[1]
      }
    }

    return foundUrl || 'N/A'
  }

  const logErrorSSR = (error: Error | string | Partial<NuxtError>, url: string | RouteLocationNormalized) => {
    if (import.meta.client) return

    // [DEBUG] 진입 확인용 로그 (터미널에서 확인)
    console.log('[logErrorSSR] Called. Error:', typeof error === 'string' ? error : (error as any)?.message)

    const returnMessage = (error as any)?.data?.message
    // console.log('[logErrorSSR] returnMessage and data :', returnMessage, (error as any)?.data)
    if (returnMessage && exceptLogging(returnMessage)) {
      console.log('[logErrorSSR] Skipped by exceptLogging:', returnMessage)
      return
    }

    try {
      const nuxtApp = useNuxtApp()
      const reqHeaders = useRequestHeaders(['user-agent', 'host'])

      const getStatusCode = (err: any) => {
        return err?.statusCode || err?.status || (err?.response ? err.response.status : undefined) || 'unknown'
      }

      const errorLog = {
        message: typeof error === 'string' ? error : (error as any)?.message,
        statusCode: getStatusCode(error),
        apiUrl: getApiUrl(error),
        pageUrl: typeof url === 'string' ? url : url.fullPath,
        pageDomain: reqHeaders['host'],
        timestamp: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
        userAgent: reqHeaders['user-agent'] || 'N/A',
        returnMessage: (error as any)?.data?.message || (error as any)?.statusMessage || '',
        stack: error instanceof Error ? error.stack : (typeof error === 'object' && error !== null ? JSON.stringify(error) : safeToString(error))
      }


      Promise.all([import('node:fs'), import('node:path')]).then(([fs, path]) => {
        const logDir = `E:\\PHP_ERROR\\${appConfig.dbDomain}\\logErrorSSR`

        const today = new Date()
        const year = today.getFullYear()
        const month = String(today.getMonth() + 1).padStart(2, '0')
        const day = String(today.getDate()).padStart(2, '0')
        const fileName = `error_${year}-${month}-${day}.log`
        const filePath = path.join(logDir, fileName)

        // 가독성을 높인 멀티라인 로그 포맷 (윈도우 줄바꿈 \r\n 적용)
        const logHeader = `[${errorLog.timestamp}] [${errorLog.statusCode}] ${errorLog.message}`
        const logBody = [
          ` - Domain: ${errorLog.pageDomain}`,
          ` - Page  : ${errorLog.pageUrl}`,
          ` - API   : ${errorLog.apiUrl}`,
          ` - Msg   : ${errorLog.returnMessage || 'no-detail'}`,
          ` - UA    : ${errorLog.userAgent}`,
          ` - Details: ${errorLog.stack}`,
          `--------------------------------------------------------------------------------------`
        ].join('\r\n')

        const logContent = `${logHeader}\r\n${logBody}\r\n`

        try {
          if (!fs.existsSync(logDir)) {
            fs.mkdirSync(logDir, { recursive: true })
          }
          fs.appendFileSync(filePath, logContent, 'utf8')
        } catch (fileErr) {
          console.error('[SSR_FILE_LOG_ERROR]', fileErr)
        }
      }).catch(err => console.error('[SSR_LOG_MODULE_LOAD_FAIL]', err))
    } catch (e) {
      console.error('[SSR_LOG_LOGIC_FAIL]', e)
    }
  }

  const logErrorClient = (error: Error | string | Partial<NuxtError>, url: string | RouteLocationNormalized) => {
    if (import.meta.server) return

    const returnMessage = (error as any)?.data?.message
    if (returnMessage && exceptLogging(returnMessage)) { //로깅할 필요가 없는 케이스면 여기서 제외
      // return
    }


    const errorLog = {
      message: typeof error === 'string' ? error : (error as any)?.message,
      statusCode: (error as any)?.statusCode,
      apiUrl: getApiUrl(error),
      pageUrl: typeof url === 'string' ? url : url.fullPath,
      pageDomain: (import.meta.client) ? window.location.host : 'N/A',
      timestamp: new Date().toISOString(),
      userAgent: (import.meta.client) ? navigator.userAgent : 'N/A',
      returnMessage: (error as any)?.data?.message,
      rawBody: safeToString(error),
      /**
       * 2026-08-25 [진단정보 유실] `rawBody` 는 `error.toString()` 이라 **스택이 들어있지 않다.**
       *
       * 그래서 Vue 런타임 안쪽에서 터진 TypeError 처럼(예: `null is not an object (evaluating 'o.emitsOptions')`)
       * 메시지만으로는 어느 컴포넌트인지 알 수 없는 클라이언트 에러가 CSR 로그에 아무 단서도 남기지 못했다.
       * SSR 쪽 `logErrorSSR` 은 이미 같은 이름의 `stack` 을 보내 ` - Details:` 로 기록하고 있었으므로
       * 양쪽 포맷을 맞춘다. 수신부는 `doyac/laravel/routes/api.php` 의 `error/log` 라우트.
       *
       * `JSON.stringify` 는 순환참조에서 던지므로 반드시 감싼다 — `logErrorSSR` 은 함수 전체가
       * try/catch 안이라 괜찮지만 이 함수는 `onErrorCaptured` 에서 직접 불려 보호막이 없다.
       * 여기서 던지면 에러 처리 도중에 또 에러가 나 원래 에러가 통째로 묻힌다(KC-004 와 같은 모양).
       */
      stack: (() => {
        if (error instanceof Error) { return error.stack }
        try {
          return typeof error === 'object' && error !== null ? JSON.stringify(error) : safeToString(error)
        } catch (e) {
          return safeToString(error)
        }
      })()
    }

    $dy__fetch(`${appConfig.API_BASE_URL}/api/error/log`, { method: 'post', body: errorLog })
      .then((response) => {
        console.log('error log sent', response)
      })
      .catch((error) => {
        console.error('error log send failed', error)
      })

  }

  const exceptLogging = (message: string) => {
    if (message.includes('마감된 이벤트입니다')) {
      return true
    }

    return false
  }

  const showPageError = (error: Error | string | Partial<NuxtError>, url: string | RouteLocationNormalized, useThrow = false) => {
    // 2026-10-08 [센트리 NUXT3-DOYACWEB-11G] 화면을 떠나서 버려진 요청은 오류가 아니다. @see libs/discarded-request.ts
    //   여기로 오면 «이미 떠난 주소» 에 에러가 기록돼(errorStore.put) 그 주소로 돌아왔을 때 에러 화면이 뜨고,
    //   봇 판정이면 showError() 로 **지금 보고 있는 다음 화면**이 통째로 에러 페이지가 된다. 둘 다 막는다.
    //   🔴 useThrow 면 종전처럼 던진다 — 호출부가 «여기서 멈춘다» 를 전제로 짜여 있다(기록·로그만 건너뛴다).
    if (isDiscardedRequestError(error)) {
      if (useThrow) throw error
      return
    }

    if (import.meta.server) { //쇼페이지 에러로 빠지는 경우 dyDefault에서 핸들링되지 못하기에 로그 코드를 추가
      logErrorSSR(error, url)
    } else {
      logErrorClient(error, url)
    }

    const userStore = useDYUserStore()
    if (import.meta.server || !userStore.isHuman) {
      if (useThrow) {
        throw createError(error)
      } else {
        showError(error)
      }
    } else {
      const errorStore = useDYErrorStore()
      errorStore.put(typeof error == 'string' ? createError(error) : error, url, useThrow)
    }
  }

  const makeAuthMiddleware = (auth?: string | AuthCheckParams | boolean, opts?: MakeAuthMiddlewareOptions): RouteMiddleware => {
    const _opts = { autoNavigateLoginPage: true, loginUrl: '/login', ...opts }

    return (to, from) => {
      if (import.meta.server) { // 서버에서 동작하는 경우에는 사용자 정보가 없으므로 그냥 넘어감.
        return
      }

      const userStore = useDYUserStore()
      const errorStore = useDYErrorStore()

      // 클라이언트이더라도 최초 페이지 접속 시에는 유저 정보가 없는 상태로 미들웨어가 동작할 수 있음.
      // 유저 정보는 미들웨어 동작 후 페이지 마운트가 일어난 후에 들어오기 때문에 유저 정보가 없다면
      // 그냥 넘겨야 함.
      const nuxtApp = useNuxtApp()
      if (import.meta.client && ((nuxtApp.isHydrating && nuxtApp.payload.serverRendered) || !userStore.isPrepared)) {
        return
      }

      const { hasAuth } = useDYAuth()
      const fromDynamicLink = Array.isArray(to.query?.fromDynamicLink)
        ? to.query?.fromDynamicLink?.[0]
        : to.query?.fromDynamicLink
      const addFromDynamicLink = (url: string) => {
        if (!fromDynamicLink || typeof fromDynamicLink !== 'string') {
          return url
        }

        try {
          const parsed = new URL(url, 'http://dy.local')
          parsed.searchParams.set('fromDynamicLink', fromDynamicLink)
          return `${parsed.pathname}${parsed.search}${parsed.hash}`
        } catch (error) {
          const encoded = encodeURIComponent(fromDynamicLink)
          return url.includes('?') ? `${url}&fromDynamicLink=${encoded}` : `${url}?fromDynamicLink=${encoded}`
        }
      }

      if (userStore.isSignedIn) {
        if (auth && !hasAuth(auth)) {
          errorStore.lastGlobalError = { statusCode: 403 }
          errorStore.lastGlobalErrorUrl = to.fullPath // 2026-08-25 신고 시 '거부당한 페이지' 를 알기 위함
          return navigateTo(addFromDynamicLink('/error'))
        }
      } else {
        if (_opts?.autoNavigateLoginPage) {
          if (!hasAuth(auth)) {
            const cookie = useCookie('auth.redirect')
            cookie.value = to.fullPath
            const basePath = useCookie('auth.basePath')
            basePath.value = useDYAppConfig().basePath
            return navigateTo(addFromDynamicLink(_opts.loginUrl))
          }
        } else {
          errorStore.lastGlobalError = { statusCode: 401 }
          errorStore.lastGlobalErrorUrl = to.fullPath // 2026-08-25 신고 시 '거부당한 페이지' 를 알기 위함
          return navigateTo(addFromDynamicLink('/error'))
        }
      }
    }
  }

  const notifyError = (error: NuxtError | string) => {
    // 2026-10-08 [센트리 NUXT3-DOYACWEB-11G] 화면을 떠나서 버려진 요청은 오류가 아니다 — 알리면 **다음 화면에**
    //   가짜 «요청한 결과를 찾을 수 없습니다» 가 뜬다(게시판 수정화면에서 빨리 뒤로 가면 상세에 떴다). @see libs/discarded-request.ts
    if (isDiscardedRequestError(error)) return
    if (import.meta.client) { // SSR에서 호출되면 에러 유발
      notify({ type: 'error', text: typeof error === 'string' ? error : (error as any)?.message })
    }
  }

  /**
   * isHuman 처리를 안할 경우 봇 접근시 에러남
   * @param value 
   * @returns 
   */
  const DYDomPurify = (value: string) => {
    const userStore = useDYUserStore()
    return userStore.isHuman ? domPurify(value) : value
  }

  const log = (message?: any, ...optionalParams: any[]) => {
    logToFile(appConfig.appName, message, ...optionalParams)
  }

  const _tryReadJson = async (typedPk: string, subPath: string) => {
    const structuredDataApi = useDYStructuredDataApi()

    if (process.dev && subPath !== 'json') { //json 폴더의 숏컷이나 배너등은 어차피 여기로 보내도 못 만듦
      const json = await structuredDataApi.get(typedPk)
      return json
    }

    const path = `D:/APM_Setup/home/hosting_users/${appConfig.dbDomain}/mercury/storage/app/public/${subPath}/${typedPk}.json`
    return await tryReadJson(path)
  }

  const readJsonAndMakeFile = async (typedPk: string, expiryDays?: number) => {
    const structuredDataApi = useDYStructuredDataApi()

    if (typedPk && typedPk.includes('-{}-')) {
      const msg = `[CORRUPTED_TYPED_PK_IN_UTILS] Value: ${typedPk}`;
      console.error(msg);
      if (import.meta.server) {
        log(msg, new Error().stack);
      }
    }
    
    // console.log('readJsonAndMakeFile::start', typedPk, expiryDays)

    try {
      let json
      // expiryDays가 음수이면 강제 갱신이므로 기존 파일을 읽으려 시도하지 않고 바로 요청함
      if (expiryDays !== undefined && expiryDays < 0) {
        return await structuredDataApi.get(typedPk, expiryDays)
      }

      json = await _tryReadJson(typedPk, 'ld+json')
      if (json) { ///새로 만들라고 요청만 보내면됨.
        await structuredDataApi.get(typedPk, expiryDays, true) //필요하다면 만들어달라고 요청함.(ex)유효기간 만료)
        return json //실질적으로는 새로 만들기 전의 json
      } else {
        //만들어서라도 가져와. 근데 캐시를 사용하므로 이전에 만들어진 캐시가있으면 서버에 로그 찍어봐야 안찍힐 수 있음.
        return await structuredDataApi.get(typedPk, expiryDays)
      }
    } catch (e) {
      console.error('readJsonAndMakeFile ERROR', e)
    }
  }

  return {
    makePageKey, showPageError, makeAuthMiddleware, notifyError,
    getCFImage, appLink, findIpForSSR, findIpForSPA, formatDate, getFullDomain, join, replaceTags, stripTag, numberFormat, uniqueId, isIpInCidr,
    DYDomPurify,
    log, logErrorSSR, logErrorClient, tryReadJson: _tryReadJson, readJsonAndMakeFile
  }
}
